import { Permission, has } from "@entities/role";
import { ApiError, api, type Channel, type Uuid } from "@shared/api";
import { toggleMute, volumeKey } from "@shared/media";
import { SEPARATOR, type MenuItem } from "@shared/ui/Menu";

/**
 * Пункты меню участника звонка — одни на плитку в звонке и на строку под
 * голосовым каналом в списке, как в Discord.
 *
 * Модераторские идут за разделителем и приходят по праву: нет права — пункта
 * нет, а не серый. Старшинство клиент не считает — его проверит сервер.
 */
export function voiceMemberItems({
  id,
  permissions,
  restriction,
  moves,
  onOpenProfile,
  onWriteDirect,
  onBan,
  onModerate,
}: {
  id: Uuid;
  permissions: number;
  /** Заглушён и оглушён ли он на сервере — для галочек. */
  restriction: { muted: boolean; deafened: boolean };
  /** Куда можно перенести: другие голосовые каналы сервера. */
  moves: Channel[];
  onBan: (id: Uuid) => void;
  onOpenProfile: (id: Uuid) => void;
  onWriteDirect: (id: Uuid) => void;
  onModerate: (action: VoiceAction) => void;
}): MenuItem[] {
  const items: MenuItem[] = [
    { label: "Заглушить для себя", onPick: () => toggleMute(volumeKey(id, false)) },
    { label: "Профиль", onPick: () => onOpenProfile(id) },
    { label: "Написать лично", onPick: () => onWriteDirect(id) },
    { label: "Позвать в другой канал", kind: "plan" },
  ];

  const powers: MenuItem[] = [];
  if (has(permissions, Permission.MuteMembers)) {
    powers.push({
      label: "Заглушить на сервере",
      choice: true,
      on: restriction.muted,
      onPick: () => onModerate({ kind: "voice", id, change: { muted: !restriction.muted } }),
    });
  }
  if (has(permissions, Permission.DeafenMembers)) {
    powers.push({
      label: "Оглушить на сервере",
      choice: true,
      on: restriction.deafened,
      onPick: () => onModerate({ kind: "voice", id, change: { deafened: !restriction.deafened } }),
    });
  }
  if (has(permissions, Permission.MoveMembers) && moves.length) {
    powers.push({
      label: "Перенести в канал",
      items: moves.map((channel) => ({
        label: channel.name,
        onPick: () => onModerate({ kind: "move", id, to: channel.id }),
      })),
    });
  }
  if (powers.length) items.push(SEPARATOR, ...powers);

  // Отключает право перемещать участников, а не выгонять, — как в Discord.
  const harsh: MenuItem[] = [];
  if (has(permissions, Permission.MoveMembers)) {
    harsh.push({ label: "Отключить от звонка", kind: "danger", onPick: () => onModerate({ kind: "disconnect", id }) });
  }
  if (has(permissions, Permission.BanMembers)) {
    harsh.push({ label: "Бан на сервере", kind: "danger", onPick: () => onBan(id) });
  }
  if (harsh.length) items.push(SEPARATOR, ...harsh);

  return items;
}

/** Что модератор делает с участником звонка из его меню. */
export type VoiceAction =
  | { kind: "voice"; id: Uuid; change: { muted?: boolean; deafened?: boolean } }
  | { kind: "disconnect"; id: Uuid }
  | { kind: "move"; id: Uuid; to: Uuid };

/**
 * Выполнить действие модератора над участником звонка. Ответ — строка отказа
 * для показа или `null`, если всё прошло.
 */
export async function runVoiceAction(
  serverId: Uuid,
  channelId: Uuid,
  action: VoiceAction,
  name: string,
): Promise<string | null> {
  try {
    if (action.kind === "voice") await api.servers.voice(serverId, action.id, action.change);
    else if (action.kind === "disconnect") await api.voice.disconnect(channelId, action.id);
    else await api.voice.move(channelId, action.id, action.to);
    return null;
  } catch (error) {
    return error instanceof ApiError && error.status === 403
      ? `Не вышло: ${name} — не младше вас по роли или не может войти туда`
      : "Не вышло — попробуйте ещё раз";
  }
}
